import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { APIContants } from 'src/app/constants/api';
import {
  AiHelpAskRequest,
  AiHelpAskResponse,
  AiHelpConversationDetail,
  AiHelpConversationDetailMessage,
  AiHelpConversationItem,
  AiHelpDashboardPopularAction,
  AiHelpDashboardPopularActionsResponse,
  AiHelpDashboardSummary,
  AiHelpDashboardTryAsk,
  AiHelpHistoryEntry,
  AiHelpPromptExamples
} from '../models/ai-help-assistant.models';
import { parseAiHelpResponse, normalizeAiHelpResponse } from '@app/features/utils/ai-help-response.parser';

interface AiHelpTranscribeResponse {
  transcript?: string;
  text?: string;
  question?: string;
}

/** Raw helpdesk `GET /conversations` row (snake_case). */
interface AiHelpConversationApiItem {
  conversation_id?: unknown;
  title?: unknown;
  created_at?: unknown;
  updated_at?: unknown;
}

interface AiHelpConversationsApiResponse {
  conversations?: unknown[];
}

@Injectable({ providedIn: 'root' })
export class AiHelpAssistantApiService {
  private readonly http = inject(HttpClient);

  private readonly askUrl = `${environment.helpdeskApiUrl}/ask`;
  private readonly transcribeUrl = `${environment.helpdeskApiUrl}/transcribe`;
  private readonly downloadPdfUrl = `${environment.helpdeskApiUrl}${APIContants.DownloadAiHelpPdf}`;
  private readonly sendEmailUrl = `${environment.helpdeskApiUrl}${APIContants.SendAiHelpEmail}`;
  private readonly dashboardSummaryUrl = `${environment.helpdeskApiUrl}${APIContants.GetAiHelpDashboardSummary}`;
  private readonly dashboardPopularActionsUrl = `${environment.helpdeskApiUrl}${APIContants.GetAiHelpDashboardPopularActions}`;
  private readonly dashboardTryAskUrl = `${environment.helpdeskApiUrl}${APIContants.GetAiHelpDashboardTryAsk}`;
  private readonly promptExamplesUrl = `${environment.helpdeskApiUrl}${APIContants.GetAiHelpPromptExamples}`;
  private readonly conversationsUrl = `${environment.helpdeskApiUrl}${APIContants.GetAiHelpConversations}`;
  private readonly conversationDeleteUrl = `${environment.helpdeskApiUrl}${APIContants.DeleteAiHelpConversation}`;
  private readonly mediaBaseUrl = environment.helpdeskApiUrl;

  /** Home summary for the AI help slider (Needs Attention, etc.). */
  getDashboardSummary(): Observable<AiHelpDashboardSummary> {
    return this.http.get<AiHelpDashboardSummary>(this.dashboardSummaryUrl);
  }

  /** Popular home shortcuts for the AI help slider. */
  getDashboardPopularActions(): Observable<AiHelpDashboardPopularAction[]> {
    return this.http
      .get<AiHelpDashboardPopularActionsResponse>(this.dashboardPopularActionsUrl)
      .pipe(map((response) => response?.popularActions ?? []));
  }

  /** "Try asking" content for the AI help slider home. */
  getDashboardTryAsk(): Observable<AiHelpDashboardTryAsk> {
    return this.http.get<AiHelpDashboardTryAsk>(this.dashboardTryAskUrl);
  }

  /** Knowledge-base categories for the left “See more” panel. */
  getPromptExamples(): Observable<AiHelpPromptExamples> {
    return this.http.get<AiHelpPromptExamples>(this.promptExamplesUrl);
  }

  /** Past question / conversation history for the AI help slider. */
  getConversations(): Observable<AiHelpConversationItem[]> {
    return this.http
      .get<AiHelpConversationsApiResponse | unknown[]>(this.conversationsUrl)
      .pipe(
        map((response) => {
          const rows = Array.isArray(response)
            ? response
            : Array.isArray(response?.conversations)
              ? response.conversations
              : [];
          return rows
            .map((row) => this.normalizeConversationItem(row))
            .filter((row): row is AiHelpConversationItem => row != null);
        })
      );
  }

  /** Full thread for a past conversation (`GET /conversations/{id}`). */
  getConversationById(conversationId: string): Observable<AiHelpConversationDetail> {
    const id = conversationId.trim();
    const url = `${this.conversationsUrl}/${encodeURIComponent(id)}`;
    return this.http.get<unknown>(url).pipe(map((raw) => this.normalizeConversationDetail(raw, id)));
  }

  /** Remove a past conversation (`DELETE /conversation-delete?conversation_id=`). */
  deleteConversation(conversationId: string): Observable<void> {
    const id = conversationId.trim();
    const params = new HttpParams().set('conversation_id', id);
    return this.http.delete<void>(this.conversationDeleteUrl, { params });
  }

  /** Remove every past conversation (`DELETE /conversation-delete?all=true`). */
  deleteAllConversations(): Observable<void> {
    const params = new HttpParams().set('all', 'true');
    return this.http.delete<void>(this.conversationDeleteUrl, { params });
  }

  ask(
    question: string,
    history: AiHelpHistoryEntry[],
    isAudio = false,
    conversationId: string | null = null
  ): Observable<AiHelpAskResponse> {
    const body: AiHelpAskRequest = {
      question,
      history,
      isAudio,
      conversation_id: conversationId
    };
    return this.http.post<unknown>(this.askUrl, body).pipe(
      map((raw) => {
        const parsed = parseAiHelpResponse(raw);
        const responseConversationId = this.extractAskConversationId(raw);
        return normalizeAiHelpResponse(
          responseConversationId
            ? { ...parsed, conversationId: responseConversationId }
            : parsed,
          this.mediaBaseUrl
        );
      })
    );
  }

  /**
   * Download a PDF for a structured data reply (`POST /download-pdf`).
   * Expects a PDF blob from the helpdesk API.
   */
  downloadPdf(body: Record<string, unknown>): Observable<Blob> {
    return this.http.post(this.downloadPdfUrl, body, { responseType: 'blob' });
  }

  /** Send an assistant-drafted email (`POST /send-email`); body is the reply's `email` object. */
  sendEmail(email: Record<string, unknown>): Observable<unknown> {
    return this.http.post<unknown>(this.sendEmailUrl, email);
  }

  /**
   * Server-side STT for browsers without Web Speech Recognition
   * (Firefox / many Safari builds). Expects multipart field `audio`.
   */
  transcribe(audio: Blob, language?: string): Observable<string> {
    const formData = new FormData();
    formData.append('audio', audio, this.buildAudioFileName(audio.type));
    if (language?.trim()) {
      formData.append('language', language.trim());
    }

    return this.http.post<AiHelpTranscribeResponse>(this.transcribeUrl, formData).pipe(
      map((response) => {
        const transcript =
          response?.transcript?.trim() ||
          response?.text?.trim() ||
          response?.question?.trim() ||
          '';
        return transcript;
      })
    );
  }

  /** Pull `conversation_id` from an `/ask` response root (or nested wrapper). */
  private extractAskConversationId(raw: unknown): string | null {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return null;
    }

    const root = raw as Record<string, unknown>;
    const direct =
      (typeof root['conversation_id'] === 'string' && root['conversation_id'].trim()) ||
      (typeof root['conversationId'] === 'string' && root['conversationId'].trim()) ||
      '';
    if (direct) {
      return direct;
    }

    for (const value of Object.values(root)) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        continue;
      }
      const nested = value as Record<string, unknown>;
      const nestedId =
        (typeof nested['conversation_id'] === 'string' && nested['conversation_id'].trim()) ||
        (typeof nested['conversationId'] === 'string' && nested['conversationId'].trim()) ||
        '';
      if (nestedId) {
        return nestedId;
      }
    }

    return null;
  }

  private normalizeConversationItem(raw: unknown): AiHelpConversationItem | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }

    const row = raw as AiHelpConversationApiItem;
    const conversationId =
      typeof row.conversation_id === 'string' ? row.conversation_id.trim() : '';
    if (!conversationId) {
      return null;
    }

    const title = typeof row.title === 'string' ? row.title.trim() : '';
    const createdAt = typeof row.created_at === 'string' ? row.created_at.trim() : '';
    const updatedAt = typeof row.updated_at === 'string' ? row.updated_at.trim() : createdAt;

    return {
      conversationId,
      title,
      createdAt,
      updatedAt
    };
  }

  private normalizeConversationDetail(
    raw: unknown,
    fallbackId: string
  ): AiHelpConversationDetail {
    const root =
      raw && typeof raw === 'object' && !Array.isArray(raw)
        ? (raw as Record<string, unknown>)
        : {};

    const conversationId =
      (typeof root['conversation_id'] === 'string' && root['conversation_id'].trim()) ||
      (typeof root['id'] === 'string' && root['id'].trim()) ||
      fallbackId;

    const title = typeof root['title'] === 'string' ? root['title'].trim() : '';
    const createdAt = typeof root['created_at'] === 'string' ? root['created_at'].trim() : '';
    const updatedAt =
      typeof root['updated_at'] === 'string' ? root['updated_at'].trim() : createdAt;

    const rawMessages = this.extractConversationMessageRows(root, raw);

    const messages = rawMessages
      .map((item) => this.normalizeConversationDetailMessage(item))
      .filter((item): item is AiHelpConversationDetailMessage => item != null);

    return {
      conversationId,
      title,
      createdAt: createdAt || undefined,
      updatedAt: updatedAt || undefined,
      messages
    };
  }

  /** Prefer `messages` / `history`, else the first array of `{ role }` turns. */
  private extractConversationMessageRows(
    root: Record<string, unknown>,
    raw: unknown
  ): unknown[] {
    if (Array.isArray(root['messages'])) {
      return root['messages'];
    }
    if (Array.isArray(root['history'])) {
      return root['history'];
    }
    if (Array.isArray(root['turns'])) {
      return root['turns'];
    }
    if (Array.isArray(raw)) {
      return raw;
    }

    for (const value of Object.values(root)) {
      if (!Array.isArray(value) || value.length === 0) {
        continue;
      }
      const looksLikeTurns = value.every(
        (item) =>
          !!item &&
          typeof item === 'object' &&
          typeof (item as Record<string, unknown>)['role'] === 'string'
      );
      if (looksLikeTurns) {
        return value;
      }
    }

    return [];
  }

  private normalizeConversationDetailMessage(
    raw: unknown
  ): AiHelpConversationDetailMessage | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }

    const row = raw as Record<string, unknown>;
    const roleRaw = typeof row['role'] === 'string' ? row['role'].trim().toLowerCase() : '';
    const role: 'user' | 'assistant' | null =
      roleRaw === 'user' || roleRaw === 'human'
        ? 'user'
        : roleRaw === 'assistant' || roleRaw === 'ai' || roleRaw === 'bot'
          ? 'assistant'
          : null;

    if (!role) {
      return null;
    }

    const createdAt =
      typeof row['created_at'] === 'string'
        ? row['created_at'].trim()
        : typeof row['timestamp'] === 'string'
          ? row['timestamp'].trim()
          : undefined;

    if (role === 'user') {
      const content =
        (typeof row['content'] === 'string' && row['content'].trim()) ||
        (typeof row['question'] === 'string' && row['question'].trim()) ||
        (typeof row['text'] === 'string' && row['text'].trim()) ||
        '';
      if (!content) {
        return null;
      }
      return { role, content, createdAt };
    }

    // Assistant turns store the /ask payload under `reply` (structured reply_type).
    const reply =
      row['reply'] && typeof row['reply'] === 'object' && !Array.isArray(row['reply'])
        ? (row['reply'] as Record<string, unknown>)
        : null;

    const replyPayload = reply ?? (this.hasStructuredReplyFields(row) ? row : null);

    const content =
      (typeof row['content'] === 'string' && row['content'].trim()) ||
      (reply && typeof reply['text'] === 'string' && reply['text'].trim()) ||
      (reply && typeof reply['message'] === 'string' && reply['message'].trim()) ||
      (typeof row['answer'] === 'string' && row['answer'].trim()) ||
      (typeof row['text'] === 'string' && row['text'].trim()) ||
      (typeof row['message'] === 'string' && row['message'].trim()) ||
      '';

    if (!replyPayload && !content) {
      return null;
    }

    return {
      role,
      content,
      raw: replyPayload ?? undefined,
      createdAt
    };
  }

  private hasStructuredReplyFields(row: Record<string, unknown>): boolean {
    return (
      'reply_type' in row ||
      'data' in row ||
      'guide' in row ||
      'options' in row ||
      'summary' in row ||
      'blocks' in row
    );
  }

  private buildAudioFileName(mimeType: string): string {
    const normalized = (mimeType || '').toLowerCase();
    if (normalized.includes('mp4') || normalized.includes('m4a') || normalized.includes('aac')) {
      return 'voice-input.m4a';
    }
    if (normalized.includes('ogg')) {
      return 'voice-input.ogg';
    }
    if (normalized.includes('mpeg') || normalized.includes('mp3')) {
      return 'voice-input.mp3';
    }
    return 'voice-input.webm';
  }
}
